import { createTooltipWithTitle } from "@ui/atoms/BaseTooltip/constants";
import {
  MetricCardDelta,
  MetricCardProps,
  trendDirection
} from "@ui/molecules/MetricCard/MetricCard";
import { percent } from "@utils/metricFormat";
import { count } from "catchment-common/utils/metricFormat";
import { textAnalytics } from "catchment-common/constants/text/analytics";
import { textEnvironmental } from "catchment-common/constants/text/environmental";
import { SegmentItem } from "@ui/molecules/ProgressBar/SegmentedProgressBar";
import { PackageType } from "catchment-common/entities/contractorPackage";
import {
  EnvironmentalCategory,
  environmentalCategoryToString,
  EnvironmentalMetricType,
  environmentalMetricTypeToUnit,
  EnvironmentalSubCategory,
  environmentalSubCategoryToString,
  EnvironmentalTargetType
} from "catchment-common/entities/environmentalCommitment";
import {
  EnergyAnalyticsSummaryLevel1LineItem,
  EnergyAnalyticsSummaryLevel2LineItem,
  EnergyAnalyticsSummaryLevel3LineItem,
  MaterialsAnalyticsSummaryLevel1LineItem,
  MaterialsAnalyticsSummaryLevel2LineItem,
  MaterialsAnalyticsSummaryLevel3LineItem,
  TransportAnalyticsSummaryLevel1LineItem,
  TransportAnalyticsSummaryLevel2LineItem,
  TransportAnalyticsSummaryLevel3LineItem,
  WasteAnalyticsSummaryLevel1LineItem,
  WasteAnalyticsSummaryLevel2LineItem,
  WasteAnalyticsSummaryLevel3LineItem,
  WaterAnalyticsSummaryLevel1LineItem,
  WaterAnalyticsSummaryLevel2LineItem,
  WaterAnalyticsSummaryLevel3LineItem
} from "catchment-common/interfaces/analytics";
import { ContractorPackageLineItem } from "catchment-common/interfaces/contractorPackage";
import { ProjectLineItem } from "catchment-common/interfaces/project";
import { IconName } from "@ui/atoms/Icon/registry";

export enum EnvironmentalTab {
  Energy = "energy",
  Water = "water",
  Waste = "waste",
  Materials = "materials",
  Transport = "transport"
}

export interface EnvironmentalTabConfig {
  label: string;
  value: EnvironmentalTab;
  icon: IconName;
}

export const EMISSIONS_TAB_TITLE = "GHG Emissions";
export const ENERGY_TAB_TITLE = "Energy, Fuels & Lubricants";
export const WATER_TAB_TITLE = "Water Usage";
export const WASTE_TAB_TITLE = "Waste";
export const MATERIALS_TAB_TITLE = "Materials";
export const TRANSPORT_TAB_TITLE = "Transport";
export const ENVIRONMENTAL_PAGE_TITLE = "Environmental Performance";

/**
 * Converts a metrics summary built for the segmented progress bar into
 * summary-grid cards.
 *
 * A straight pass-through: a segment already names what its number is, and that
 * is exactly what `MetricCard` needs, so the tile renders through the same
 * standard as every other tile in the app rather than through text its producer
 * formatted.
 */
export const toAnalyticsStatItems = (
  segments: SegmentItem[]
): MetricCardProps[] =>
  segments.map((segment) => ({
    dataTestId: segment.dataTestId,
    delta: segment.trend,
    label: segment.heading,
    progressBar: "none",
    // One line per tile, in order of preference: how the figure MOVED, else the
    // share of the total it is part of, else the window it covers.
    //
    // The last is a fallback, not a measure. A card with no earlier window has
    // no movement to state, and on the Energy tab — where every card is a
    // movement card and the five streams have no common total to be a share of
    // — that left the whole tab blank on any project reporting its first month.
    // Naming the window says something true where a "0% This Period" would not.
    subtitle: segment.trend
      ? undefined
      : (segment.subtitle ?? textEnvironmental.reportedThisPeriod),
    valueConfig: { kind: segment.kind, value: segment.value }
  }));

/**
 * How a stream's figure moved against the window before this one — the same
 * line the org Environmental cards carry. Absent when nothing was reported
 * earlier: no baseline, so the card shows the number alone rather than a rise
 * out of zero.
 */
export const environmentalMovement = (
  value: number,
  prior: number | undefined
): MetricCardDelta | undefined => {
  if (prior === undefined || prior <= 0) return undefined;
  const pc = ((value - prior) / prior) * 100;
  return {
    direction: trendDirection(pc),
    text: `${percent(Math.abs(pc))} ${textAnalytics.thisPeriod}`
  };
};

/**
 * Streams that reported for the FIRST time in this window — the line Figma gives
 * the Number of Materials card.
 *
 * An additive count, not a percentage: the card's value is itself a count of
 * streams, and "+40%" over a base of 5 is unreadable where "+2 New Items" is
 * not. It therefore does not go through {@link environmentalMovement}.
 *
 * Absent when nothing is new, and absent when there is no earlier window to be
 * new against — the same rule the movement line follows. "0 New Items" is the
 * ordinary state of a settled project and a line stating it says nothing.
 */
export const environmentalNewItems = (
  newCount: number
): MetricCardDelta | undefined =>
  newCount > 0
    ? {
        direction: "up",
        text: `${count(newCount)} ${textEnvironmental.newItems}`
      }
    : undefined;

/**
 * A stream's share of its tab's total — the line Figma gives every card that is
 * a part of the total beside it. Absent when there is no total to divide by.
 */
export const environmentalShare = (
  value: number,
  total: number
): string | undefined =>
  total > 0
    ? `${percent((value / total) * 100)} ${textEnvironmental.ofTotalValue}`
    : undefined;

/** Extra summary-grid card for the monthly-use-vs-total figure previously shown as a progress bar. */
export const buildMonthlyUseStatItem = (
  monthlyProjectUse: number,
  totalProjectUse: number
): MetricCardProps => {
  const monthlyUsePercent =
    totalProjectUse > 0
      ? Math.min((monthlyProjectUse / totalProjectUse) * 100, 100)
      : 0;
  return {
    dataTestId: "monthly-use-percent",
    label: "Monthly Use",
    progressBar: "none",
    subtitle: "% of Total Project Use",
    valueConfig: { kind: "percent", value: monthlyUsePercent }
  };
};

export const ENVIRONMENTAL_TABS: EnvironmentalTabConfig[] = [
  { icon: "energy", label: ENERGY_TAB_TITLE, value: EnvironmentalTab.Energy },
  { icon: "water", label: WATER_TAB_TITLE, value: EnvironmentalTab.Water },
  { icon: "delete", label: WASTE_TAB_TITLE, value: EnvironmentalTab.Waste },
  {
    icon: "material",
    label: MATERIALS_TAB_TITLE,
    value: EnvironmentalTab.Materials
  },
  {
    icon: "transport",
    label: TRANSPORT_TAB_TITLE,
    value: EnvironmentalTab.Transport
  }
];

/** Maps each analytics tab to the project environmental target type that enables it. */
export const ENVIRONMENTAL_TAB_TO_TARGET_TYPE: Record<
  EnvironmentalTab,
  EnvironmentalTargetType
> = {
  [EnvironmentalTab.Energy]:
    EnvironmentalTargetType.EnergyAndFuelsAndLubricants,
  [EnvironmentalTab.Materials]: EnvironmentalTargetType.Materials,
  [EnvironmentalTab.Transport]: EnvironmentalTargetType.Transport,
  [EnvironmentalTab.Waste]: EnvironmentalTargetType.Waste,
  [EnvironmentalTab.Water]: EnvironmentalTargetType.WaterUsage
};

export type GetVisibleEnvironmentalTabsOptions = {
  /**
   * When true (or when project is undefined), returns the full tab list so the
   * UI does not flicker before project/commitments are available.
   */
  deferFilter?: boolean;
};

/** Tabs to show for analytics/dashboard from in-scope environmental commitments. */
export const getVisibleEnvironmentalTabs = (
  project: ProjectLineItem | undefined,
  contractorPackage?: ContractorPackageLineItem,
  options?: GetVisibleEnvironmentalTabsOptions
): EnvironmentalTabConfig[] => {
  if (options?.deferFilter || !project) {
    return ENVIRONMENTAL_TABS;
  }

  const isSubPackage =
    contractorPackage?.packageType &&
    contractorPackage.packageType !== PackageType.HeadContractorPackage;

  const enabledTypes = isSubPackage
    ? new Set(
        (contractorPackage?.commitmentsEnvironmental ?? [])
          .map(
            (packageCommitment) =>
              (project.commitmentsEnvironmental ?? []).find(
                (projectCommitment) =>
                  projectCommitment.id ===
                  packageCommitment.projectEnvironmentalCommitmentId
              )?.targetType
          )
          .filter((targetType): targetType is EnvironmentalTargetType =>
            Boolean(targetType)
          )
      )
    : new Set(
        (project.commitmentsEnvironmental ?? []).map((c) => c.targetType)
      );

  return ENVIRONMENTAL_TABS.filter((tab) =>
    enabledTypes.has(ENVIRONMENTAL_TAB_TO_TARGET_TYPE[tab.value])
  );
};

export const ENVIRONMENTAL_CHART_COLORS = {
  neutral: "var(--mui-palette-primary-300)",
  primary: "var(--mui-palette-primary-main)",
  quaternary: "var(--mui-palette-primary-50)",
  quinary: "var(--mui-palette-primary-50)",
  secondary: "var(--mui-palette-primary-200)",
  tertiary: "var(--mui-palette-primary-400)"
};

export const tabs = ENVIRONMENTAL_TABS.map((tab) => ({ label: tab.label }));

export enum EnvironmentalQueryParams {
  Category = "category",
  SubCategory = "subCategory",
  MetricType = "metricType",
  ContractorName = "contractorName",
  ProjectEnvironmentalCommitmentId = "projectEnvironmentalCommitmentId"
}

interface EnvironmentalLevel2QueryInput {
  [EnvironmentalQueryParams.Category]: EnvironmentalCategory;
  [EnvironmentalQueryParams.SubCategory]: EnvironmentalSubCategory | string;
  [EnvironmentalQueryParams.MetricType]: EnvironmentalMetricType;
  [EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId]: string;
}

export const buildEnvironmentalLevel2QueryParams = (
  row: EnvironmentalLevel2QueryInput
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set(EnvironmentalQueryParams.Category, row.category);
  params.set(EnvironmentalQueryParams.SubCategory, row.subCategory);
  params.set(EnvironmentalQueryParams.MetricType, row.metricType);
  params.set(
    EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId,
    row[EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId]
  );
  return params;
};

interface EnvironmentalLevel3QueryInput {
  [EnvironmentalQueryParams.Category]: string;
  [EnvironmentalQueryParams.SubCategory]: string;
  [EnvironmentalQueryParams.MetricType]: string;
  [EnvironmentalQueryParams.ContractorName]: string;
  [EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId]: string;
}

export const buildEnvironmentalLevel3QueryParams = (
  data: EnvironmentalLevel3QueryInput
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set(
    EnvironmentalQueryParams.Category,
    data[EnvironmentalQueryParams.Category]
  );
  params.set(
    EnvironmentalQueryParams.SubCategory,
    data[EnvironmentalQueryParams.SubCategory]
  );
  params.set(
    EnvironmentalQueryParams.MetricType,
    data[EnvironmentalQueryParams.MetricType]
  );
  params.set(
    EnvironmentalQueryParams.ContractorName,
    data[EnvironmentalQueryParams.ContractorName]
  );
  params.set(
    EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId,
    data[EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId]
  );
  return params;
};

export const toEnvironmentalDisplayValues = ({
  category,
  metricType,
  subCategory
}: {
  category: string;
  metricType: string;
  subCategory: string;
}) => ({
  category: Object.values(EnvironmentalCategory).includes(
    category as EnvironmentalCategory
  )
    ? environmentalCategoryToString(category as EnvironmentalCategory)
    : category,
  metricType: Object.values(EnvironmentalMetricType).includes(
    metricType as EnvironmentalMetricType
  )
    ? environmentalMetricTypeToUnit(metricType as EnvironmentalMetricType)
    : metricType,
  subCategory: Object.values(EnvironmentalSubCategory).includes(
    subCategory as EnvironmentalSubCategory
  )
    ? environmentalSubCategoryToString(subCategory as EnvironmentalSubCategory)
    : subCategory
});

export const parseEnvironmentalQueryParams = (
  searchParams: URLSearchParams
) => {
  const rawCategory =
    searchParams?.get(EnvironmentalQueryParams.Category) || "";
  const rawSubCategory =
    searchParams?.get(EnvironmentalQueryParams.SubCategory) || "";
  const rawMetricType =
    searchParams?.get(EnvironmentalQueryParams.MetricType) || "";
  const contractorName =
    searchParams?.get(EnvironmentalQueryParams.ContractorName) || "";
  const projectEnvironmentalCommitmentId =
    searchParams?.get(
      EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId
    ) || "";

  const category = Object.values(EnvironmentalCategory).includes(
    rawCategory as EnvironmentalCategory
  )
    ? environmentalCategoryToString(rawCategory as EnvironmentalCategory)
    : rawCategory;
  const subCategory = Object.values(EnvironmentalSubCategory).includes(
    rawSubCategory as EnvironmentalSubCategory
  )
    ? environmentalSubCategoryToString(
        rawSubCategory as EnvironmentalSubCategory
      )
    : rawSubCategory;
  const metricType = Object.values(EnvironmentalMetricType).includes(
    rawMetricType as EnvironmentalMetricType
  )
    ? environmentalMetricTypeToUnit(rawMetricType as EnvironmentalMetricType)
    : rawMetricType;

  return {
    [EnvironmentalQueryParams.Category]: category,
    [EnvironmentalQueryParams.ContractorName]: contractorName,
    [EnvironmentalQueryParams.MetricType]: metricType,
    [EnvironmentalQueryParams.ProjectEnvironmentalCommitmentId]:
      projectEnvironmentalCommitmentId,
    [EnvironmentalQueryParams.SubCategory]: subCategory
  };
};

export const buildEnergyLevel1MetricsSummary = (
  energySummary: EnergyAnalyticsSummaryLevel1LineItem,
  priorSummary?: EnergyAnalyticsSummaryLevel1LineItem
): SegmentItem[] => {
  const moved = (
    value: number,
    key: keyof EnergyAnalyticsSummaryLevel1LineItem
  ) => environmentalMovement(value, priorSummary?.[key]);
  return [
    {
      color: ENVIRONMENTAL_CHART_COLORS.tertiary,
      dataTestId: "energy-electricity-qty",
      heading: "Electricity (kWh)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Electricity (kWh)",
        "Reported electricity quantity across the project."
      ),
      trend: moved(energySummary?.electricityTotal || 0, "electricityTotal"),
      value: energySummary?.electricityTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "energy-diesel-qty",
      heading: "Diesel (L)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Diesel (L)",
        "Reported diesel quantity across the project."
      ),
      trend: moved(energySummary?.dieselTotal || 0, "dieselTotal"),
      value: energySummary?.dieselTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "energy-petrol-qty",
      heading: "Petrol (L)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Petrol (L)",
        "Reported petrol quantity across the project."
      ),
      trend: moved(energySummary?.petrolTotal || 0, "petrolTotal"),
      value: energySummary?.petrolTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.neutral,
      dataTestId: "energy-lpg-qty",
      heading: "LPG (L)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "LPG (L)",
        "Reported LPG quantity across the project."
      ),
      trend: moved(energySummary?.lpgTotal || 0, "lpgTotal"),
      value: energySummary?.lpgTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.quaternary,
      dataTestId: "energy-natural-gas-qty",
      heading: "Natural Gas (m³)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Natural Gas (m³)",
        "Reported natural gas quantity across the project."
      ),
      trend: moved(energySummary?.naturalGasTotal || 0, "naturalGasTotal"),
      value: energySummary?.naturalGasTotal || 0
    }
  ];
};

export const buildEnergyLevel3MetricsSummary = (
  energySummary: EnergyAnalyticsSummaryLevel3LineItem,
  category: string,
  subCategory: string,
  metricType: string
): SegmentItem[] => {
  return [
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "energy-total-project-use",
      heading: `Total Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Total Project Use (${metricType})`,
        `Total ${category} (${subCategory}) usage to date for this work package.`
      ),
      value: energySummary.totalProjectUse ?? 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "energy-monthly-project-use",
      heading: `Monthly Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Monthly Project Use (${metricType})`,
        `Amount reported in the latest monthly report for ${category} (${subCategory}) in this work package.`
      ),
      value: energySummary.monthlyProjectUse ?? 0
    },
    {
      dataTestId: "energy-average-monthly-use",
      heading: `Average Monthly Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Average Monthly Use (${metricType})`,
        `Average monthly usage calculated as Total Project Use divided by the number of reports for this work package.`
      ),
      value: energySummary.averageMonthlyUse ?? 0
    }
  ];
};

export const buildEnergyLevel2MetricsSummary = (
  energySummary: EnergyAnalyticsSummaryLevel2LineItem,
  category: string,
  subCategory: string,
  metricType: string
): SegmentItem[] => {
  return [
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "energy-total-project-use",
      heading: `Total Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Total Project Use (${metricType})`,
        `Total ${category} (${subCategory}) usage to date across all in-scope work packages.`
      ),
      value: energySummary.totalProjectUse ?? 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "energy-monthly-project-use",
      heading: `Monthly Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Monthly Project Use (${metricType})`,
        `Amount reported in the latest monthly report for ${category} (${subCategory}).`
      ),
      value: energySummary.monthlyProjectUse ?? 0
    },
    {
      dataTestId: "energy-average-monthly-use",
      heading: `Average Monthly Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Average Monthly Use (${metricType})`,
        `Average monthly usage calculated as Total Project Use divided by the number of reports.`
      ),
      value: energySummary.averageMonthlyUse ?? 0
    }
  ];
};

export const buildWaterLevel1MetricsSummary = (
  waterSummary: WaterAnalyticsSummaryLevel1LineItem,
  priorSummary?: WaterAnalyticsSummaryLevel1LineItem
): SegmentItem[] => {
  const moved = (
    value: number,
    key: keyof WaterAnalyticsSummaryLevel1LineItem
  ) => environmentalMovement(value, priorSummary?.[key]);
  // Figma: the total states how it moved, each part its share of that total.
  const share = (value: number) =>
    environmentalShare(value, waterSummary.totalWater || 0);
  return [
    {
      dataTestId: "water-total",
      heading: "Total Water (kL)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total Water (kL)",
        "Total water consumed across the project."
      ),
      trend: moved(waterSummary.totalWater || 0, "totalWater"),
      value: waterSummary.totalWater || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "water-potable",
      heading: "Potable (kL)",
      kind: "quantity",
      subtitle: share(waterSummary.potableTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Potable (kL)",
        "Total potable water consumed."
      ),
      value: waterSummary.potableTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "water-non-potable",
      heading: "Non-Potable (kL)",
      kind: "quantity",
      subtitle: share(waterSummary.nonPotableTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Non-Potable (kL)",
        "Total non-potable water consumed."
      ),
      value: waterSummary.nonPotableTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.neutral,
      dataTestId: "water-reused-on-site",
      heading: "Reused On Site (kL)",
      kind: "quantity",
      subtitle: share(waterSummary.reusedOnSiteTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Reused On Site (kL)",
        "Total water reused on site."
      ),
      value: waterSummary.reusedOnSiteTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.tertiary,
      dataTestId: "water-discharge",
      heading: "Discharge (kL)",
      kind: "quantity",
      subtitle: share(waterSummary.waterDischargeTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Discharge (kL)",
        "Total water discharged."
      ),
      value: waterSummary.waterDischargeTotal || 0
    }
  ];
};

export const buildWaterLevel3MetricsSummary = (
  waterSummary: WaterAnalyticsSummaryLevel3LineItem,
  category: string,
  subCategory: string,
  metricType: string
): SegmentItem[] => {
  return [
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "water-total-project-use",
      heading: `Total Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Total Project Use (${metricType})`,
        `Total ${category} (${subCategory}) usage to date for this work package.`
      ),
      value: waterSummary.totalProjectUse ?? 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "water-monthly-project-use",
      heading: `Monthly Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Monthly Project Use (${metricType})`,
        `Current month ${category} (${subCategory}) usage for this work package.`
      ),
      value: waterSummary.monthlyProjectUse ?? 0
    },
    {
      dataTestId: "water-average-monthly-use",
      heading: `Average Monthly Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Average Monthly Use (${metricType})`,
        `Average monthly ${category} (${subCategory}) usage for this work package.`
      ),
      value: waterSummary.averageMonthlyUse ?? 0
    }
  ];
};

export const buildWaterLevel2MetricsSummary = (
  waterSummary: WaterAnalyticsSummaryLevel2LineItem,
  category: string,
  subCategory: string,
  metricType: string
): SegmentItem[] => {
  return [
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "water-total-project-use",
      heading: `Total Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Total Project Use (${metricType})`,
        `Total ${category} (${subCategory}) usage to date across all in-scope work packages.`
      ),
      value: waterSummary.totalProjectUse ?? 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "water-monthly-project-use",
      heading: `Monthly Project Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Monthly Project Use (${metricType})`,
        `Amount reported in the latest monthly report for ${category} (${subCategory}).`
      ),
      value: waterSummary.monthlyProjectUse ?? 0
    },
    {
      dataTestId: "water-average-monthly-use",
      heading: `Average Monthly Use (${metricType})`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Average Monthly Use (${metricType})`,
        `Average monthly usage calculated as Total Project Use divided by the number of reports.`
      ),
      value: waterSummary.averageMonthlyUse ?? 0
    }
  ];
};

type WasteCategoryCardConfig = {
  dataTestId: string;
  heading: string;
  tooltip: string;
  getValue: (summary: WasteAnalyticsSummaryLevel1LineItem) => number;
  color: string;
};

const WASTE_CATEGORY_CARD_CONFIG: Partial<
  Record<EnvironmentalCategory, WasteCategoryCardConfig>
> = {
  [EnvironmentalCategory.ConstructionDemolitionWaste]: {
    color: ENVIRONMENTAL_CHART_COLORS.primary,
    dataTestId: "waste-construction-demolition",
    getValue: (summary) => summary.constructionDemolitionWaste || 0,
    heading: "Construction/Demolition (t)",
    tooltip: "Total construction and demolition waste."
  },
  [EnvironmentalCategory.OfficeWaste]: {
    color: ENVIRONMENTAL_CHART_COLORS.secondary,
    dataTestId: "waste-office",
    getValue: (summary) => summary.officeWaste || 0,
    heading: "Office (t)",
    tooltip: "Total office waste."
  },
  [EnvironmentalCategory.SolidLiquidMixedWaste]: {
    color: ENVIRONMENTAL_CHART_COLORS.tertiary,
    dataTestId: "waste-solid-liquid-mixed",
    getValue: (summary) => summary.solidLiquidMixedWaste || 0,
    heading: "Solid & Liquid Mixed (t)",
    tooltip: "Total solid and liquid mixed waste."
  },
  [EnvironmentalCategory.SpoilFillSoilMaterial]: {
    color: ENVIRONMENTAL_CHART_COLORS.neutral,
    dataTestId: "waste-spoil-fill",
    getValue: (summary) => summary.spoilFillMaterialWaste || 0,
    heading: "Spoil/Fill/Soil (t)",
    tooltip: "Total spoil, fill and soil material waste."
  }
};

export const computeWasteCategoryTotalsFromRows = (
  rows: ReadonlyArray<{
    category: EnvironmentalCategory;
    totalWaste: number;
  }>
): Partial<Record<EnvironmentalCategory, number>> => {
  const totals: Partial<Record<EnvironmentalCategory, number>> = {};
  for (const row of rows) {
    totals[row.category] = (totals[row.category] ?? 0) + (row.totalWaste ?? 0);
  }
  return totals;
};

export const buildWasteMetricsSummary = (
  wasteSummary: WasteAnalyticsSummaryLevel1LineItem,
  chosenCategories: EnvironmentalCategory[] = [],
  options?: {
    hideEmissionsRow?: boolean;
    /**
     * Per-category totals derived from the table rows. When provided, these
     * take precedence over the backend summary's per-category rollups so the
     * UI stays correct even if a new waste sub-category is missing from the
     * backend's `wasteParentCategoryBySubCategory` mapping.
     */
    categoryTotals?: Partial<Record<EnvironmentalCategory, number>>;
    /** The same summary over the previous window, for each card's movement. */
    priorSummary?: WasteAnalyticsSummaryLevel1LineItem;
  }
): SegmentItem[] => {
  const prior = options?.priorSummary;
  const rows: SegmentItem[] = [
    {
      dataTestId: "waste-total",
      heading: "Total Quantity",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total Quantity",
        "Total waste generated across the project."
      ),
      trend: environmentalMovement(
        wasteSummary.totalWaste || 0,
        prior?.totalWaste
      ),
      value: wasteSummary.totalWaste || 0
    }
  ];

  const seenCategories = new Set<EnvironmentalCategory>();
  for (const category of chosenCategories) {
    if (seenCategories.has(category)) continue;
    seenCategories.add(category);
    const cardConfig = WASTE_CATEGORY_CARD_CONFIG[category];
    if (!cardConfig) continue;
    const override = options?.categoryTotals?.[category];
    const value =
      override !== undefined ? override : cardConfig.getValue(wasteSummary);
    rows.push({
      color: cardConfig.color,
      dataTestId: cardConfig.dataTestId,
      heading: cardConfig.heading,
      kind: "quantity",
      // Figma states each stream's share of total waste rather than how it
      // moved — a share needs no prior window, so it survives the override case
      // that made a movement unsafe here.
      subtitle: environmentalShare(value, wasteSummary.totalWaste || 0),
      tooltipText: createTooltipWithTitle(
        cardConfig.heading,
        cardConfig.tooltip
      ),
      value: value
    });
  }

  if (!options?.hideEmissionsRow) {
    rows.push({
      dataTestId: "waste-emissions",
      heading: "Emissions (tCO\u2082e)",
      kind: "emissions",
      // A total in its own unit, so it states how it moved, not a share of the
      tooltipText: createTooltipWithTitle(
        "Emissions (tCO\u2082e)",
        "Total waste emissions."
      ),
      // tonnage beside it.
      trend: environmentalMovement(
        wasteSummary.totalEmissions || 0,
        prior?.totalEmissions
      ),
      value: wasteSummary.totalEmissions || 0
    });
  }

  return rows;
};

export const buildWasteLevel1MetricsSummary = (
  wasteSummary: WasteAnalyticsSummaryLevel1LineItem,
  chosenCategories: EnvironmentalCategory[] = [],
  categoryTotals?: Partial<Record<EnvironmentalCategory, number>>,
  priorSummary?: WasteAnalyticsSummaryLevel1LineItem
): SegmentItem[] =>
  buildWasteMetricsSummary(wasteSummary, chosenCategories, {
    categoryTotals,
    priorSummary
  });

export const buildWasteLevel2MetricsSummary = (
  wasteSummary: WasteAnalyticsSummaryLevel2LineItem,
  chosenCategories: EnvironmentalCategory[] = [],
  categoryTotals?: Partial<Record<EnvironmentalCategory, number>>
): SegmentItem[] =>
  buildWasteMetricsSummary(wasteSummary, chosenCategories, { categoryTotals });

export const buildWasteLevel3MetricsSummary = (
  wasteSummary: WasteAnalyticsSummaryLevel3LineItem,
  chosenCategories: EnvironmentalCategory[] = [],
  categoryTotals?: Partial<Record<EnvironmentalCategory, number>>
): SegmentItem[] =>
  buildWasteMetricsSummary(wasteSummary, chosenCategories, { categoryTotals });

/** The material streams the summary reports, and the only things the Number of
 * Materials card counts. Named once so the count and its "New Items" line can
 * never be taken over different sets. */
const MATERIAL_STREAM_KEYS = [
  "concreteTotal",
  "steelTotal",
  "timberTotal",
  "otherTotal"
] as const satisfies readonly (keyof MaterialsAnalyticsSummaryLevel1LineItem)[];

export const buildMaterialsLevel1MetricsSummary = (
  materialsSummary: MaterialsAnalyticsSummaryLevel1LineItem,
  priorSummary?: MaterialsAnalyticsSummaryLevel1LineItem
): SegmentItem[] => {
  const moved = (
    value: number,
    key: keyof MaterialsAnalyticsSummaryLevel1LineItem
  ) => environmentalMovement(value, priorSummary?.[key]);
  return [
    {
      dataTestId: "materials-total",
      heading: "Total Materials (t)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total Materials (t)",
        "Total Materials quantity."
      ),
      trend: moved(materialsSummary?.totalMaterials || 0, "totalMaterials"),
      value: materialsSummary?.totalMaterials || 0
    },
    {
      // Figma adds this card. The summary carries no item count, so it names
      // the material STREAMS that reported — concrete, steel, timber, other.
      dataTestId: "materials-count",
      heading: textEnvironmental.numberOfMaterials,
      kind: "count",
      tooltipText: createTooltipWithTitle(
        textEnvironmental.numberOfMaterials,
        "Material streams with a reported quantity."
      ),
      // "+N New Items": streams reporting now that reported nothing before.
      // Counted per stream rather than as (now - before), so a project that
      // picked up timber and dropped steel reads as one new item rather than
      // as no change — the card is about what appeared, not net movement.
      //
      // No prior summary means no window to be new AGAINST, so there is no
      // line — the same rule `environmentalMovement` follows. Without this
      // guard an absent prior read as all-zero and every reporting stream
      // looked new, so a project's first window claimed every material it had
      // ever recorded as an arrival.
      trend:
        priorSummary === undefined
          ? undefined
          : environmentalNewItems(
              MATERIAL_STREAM_KEYS.filter(
                (key) =>
                  (materialsSummary?.[key] ?? 0) > 0 &&
                  (priorSummary[key] ?? 0) <= 0
              ).length
            ),
      value: MATERIAL_STREAM_KEYS.filter(
        (key) => (materialsSummary?.[key] ?? 0) > 0
      ).length
    },
    {
      dataTestId: "materials-total-emissions",
      heading: "Total Emissions (tCO\u2082e)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Total Emissions (tCO\u2082e)",
        "Materials scope 3 emissions total."
      ),
      trend: moved(materialsSummary?.totalEmissions || 0, "totalEmissions"),
      value: materialsSummary?.totalEmissions || 0
    }
  ];
};

export const buildMaterialsLevel3MetricsSummary = (
  materialsSummary: MaterialsAnalyticsSummaryLevel3LineItem,
  typeLabel: string
): SegmentItem[] => {
  return [
    {
      dataTestId: "materials-total-type",
      heading: `Total ${typeLabel} (t)`,
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        `Total ${typeLabel} (t)`,
        `Total ${typeLabel} quantity.`
      ),
      value: materialsSummary?.totalMaterials || 0
    },
    {
      dataTestId: "materials-emissions-intensity",
      heading: "Emissions Intensity (kgCO\u2082e/t)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Emissions Intensity (kgCO\u2082e/t)",
        `Emissions per unit of ${typeLabel}.`
      ),
      value: materialsSummary?.emissionsIntensity || 0
    },
    {
      dataTestId: "materials-total-emissions",
      heading: "Total Emissions (tCO\u2082e)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Total Emissions (tCO\u2082e)",
        `Total Scope 3 emissions from ${typeLabel}.`
      ),
      value: materialsSummary?.totalEmissions || 0
    }
  ];
};

export const buildMaterialsLevel2MetricsSummary = (
  materialsSummary: MaterialsAnalyticsSummaryLevel2LineItem,
  typeLabel: string
): SegmentItem[] => {
  return buildMaterialsLevel3MetricsSummary(materialsSummary, typeLabel);
};

export const buildTransportLevel1MetricsSummary = (
  transportSummary: TransportAnalyticsSummaryLevel1LineItem,
  priorSummary?: TransportAnalyticsSummaryLevel1LineItem
): SegmentItem[] => {
  const moved = (
    value: number,
    key: keyof TransportAnalyticsSummaryLevel1LineItem
  ) => environmentalMovement(value, priorSummary?.[key]);
  // Figma: the total states how it moved, each mode its share of that total.
  const transportShare = (value: number) =>
    environmentalShare(value, transportSummary?.totalTransport || 0);
  return [
    {
      dataTestId: "transport-total",
      heading: "Total Transport (t·km)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total Transport (t·km)",
        "Total Transport activity."
      ),
      trend: moved(transportSummary?.totalTransport || 0, "totalTransport"),
      value: transportSummary?.totalTransport || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.primary,
      dataTestId: "transport-road",
      heading: "Road (t·km)",
      kind: "quantity",
      subtitle: transportShare(transportSummary?.roadTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Road (t·km)",
        "Road Transport activity."
      ),
      value: transportSummary?.roadTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.secondary,
      dataTestId: "transport-rail",
      heading: "Rail (t·km)",
      kind: "quantity",
      subtitle: transportShare(transportSummary?.railTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Rail (t·km)",
        "Rail Transport activity."
      ),
      value: transportSummary?.railTotal || 0
    },
    {
      color: ENVIRONMENTAL_CHART_COLORS.neutral,
      dataTestId: "transport-sea",
      heading: "Sea (t·km)",
      kind: "quantity",
      subtitle: transportShare(transportSummary?.seaTotal || 0),
      tooltipText: createTooltipWithTitle(
        "Sea (t·km)",
        "Sea Transport activity."
      ),
      value: transportSummary?.seaTotal || 0
    }
  ];
};

export const buildTransportLevel2MetricsSummary = (
  transportSummary: TransportAnalyticsSummaryLevel2LineItem,
  category: string,
  subCategory: string
): SegmentItem[] => {
  return [
    {
      dataTestId: "transport-total-emissions",
      // The `emissions` kind, as every other carbon total: this tile was once a
      // whole number, which rounded a real 0.4 tCO₂e to "0" while the level
      heading: "Total Emissions (tCO\u2082e)",
      // below it showed "0.40".
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Total Emissions (tCO\u2082e)",
        `Total Scope 3 Emissions from ${category} (${subCategory}).`
      ),
      value: transportSummary?.totalEmissions || 0
    },
    {
      dataTestId: "transport-total",
      heading: "Total (t·km)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total (t·km)",
        "Total freight times distance."
      ),
      value: transportSummary?.total || 0
    },
    {
      dataTestId: "transport-emissions-intensity",
      heading: "Emissions Intensity (tCO\u2082e/t·km)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Emissions Intensity (tCO\u2082e/t·km)",
        `Emissions per unit of ${category} (${subCategory}).`
      ),
      value: transportSummary?.emissionIntensity || 0
    }
  ];
};

export const buildTransportLevel3MetricsSummary = (
  transportSummary: TransportAnalyticsSummaryLevel3LineItem,
  category: string,
  subCategory: string
): SegmentItem[] => {
  return [
    {
      dataTestId: "transport-total-emissions",
      heading: "Total Emissions (tCO\u2082e)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Total Emissions (tCO\u2082e)",
        `Total Scope 3 Emissions from ${category} (${subCategory}) for this work package.`
      ),
      value: transportSummary?.totalEmissions || 0
    },
    {
      dataTestId: "transport-total",
      heading: "Total (t·km)",
      kind: "quantity",
      tooltipText: createTooltipWithTitle(
        "Total (t·km)",
        "Total freight times distance for this work package."
      ),
      value: transportSummary?.total || 0
    },
    {
      dataTestId: "transport-emissions-intensity",
      heading: "Emissions Intensity (tCO\u2082e/t·km)",
      kind: "emissions",
      tooltipText: createTooltipWithTitle(
        "Emissions Intensity (tCO\u2082e/t·km)",
        `Emissions per unit of ${category} (${subCategory}).`
      ),
      value: transportSummary?.emissionIntensity || 0
    }
  ];
};
